import {
	ChangeDetectorRef,
	Directive,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Renderer2,
	SimpleChanges,
} from '@angular/core';
import { CmLocalizationService, CmSettingsService, PortalCoreFeature, PublicLocalization } from '@ats/cm/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, switchMap, take, withLatestFrom } from 'rxjs/operators';

/** @deprecated */
@UntilDestroy()
@Directive({
    selector: '[cmLocalize]',
    providers: [],
    standalone: false
})
export class CmLocalizeDirective implements OnInit, OnDestroy, OnChanges {
	@Input() cmLocalize: string;
	@Input() default: string;
	@Input() htmlAllowed = false;
	@Input() locReadonly = false;
	localization: PublicLocalization;
	el: any;
	localizedValue: string = '';
	clickListenerFn: () => void;
	keyboardListenerFn: () => void;
	private keyChangesSubj$ = new BehaviorSubject<string>('');
	private keyChanges$: Observable<string> = this.keyChangesSubj$.pipe(distinctUntilChanged());
	private readonly ariaDefaultText: string = 'Update localization value for:';
	readonly locFeature: PortalCoreFeature;

	constructor(
		private localizationService: CmLocalizationService,
		private element: ElementRef,
		private renderer: Renderer2,
		private cd: ChangeDetectorRef,
		private settings: CmSettingsService
	) {
		this.locFeature = this.settings.portalCoreFeature;
	}

	ngOnInit() {
		this.updateHtmlClass();

		this.keyChanges$
			.pipe(
				untilDestroyed(this),
				switchMap(key =>
					this.localizationService
						.findLocalization$(key)
						.pipe(withLatestFrom(this.localizationService.isLocalizing$))
				)
			)
			.subscribe(([localization, isLocalizing]) => this.localize(localization, isLocalizing));

		this.localizationService.isLocalizing$
			.pipe(untilDestroyed(this))
			.subscribe((showing: boolean) => this.toggleInlineLocalization(showing));
	}

	ngOnChanges(changes: SimpleChanges) {
		if ((((changes || {}).htmlAllowed || {}).currentValue || []).length || 0 > 0) {
			this.updateHtmlClass();
		}

		if (changes.cmLocalize && changes.cmLocalize.currentValue && changes.cmLocalize.currentValue.length > 0) {
			this.keyChangesSubj$.next(changes.cmLocalize.currentValue);
		}
	}

	private updateHtmlClass(): void {
		if (this.htmlAllowed) {
			this.renderer.addClass(this.element.nativeElement, 'cm-localized-content');
		} else {
			this.renderer.removeClass(this.element.nativeElement, 'cm-localized-content');
		}
	}

	private async localize(localization: PublicLocalization, isLocalizing: boolean): Promise<any> {
		this.localization = localization;
		let localizedValue = '';

		// Localization found and has a value
		if ((this.localization || {}).value || (this.localization || {}).value?.trim() === '') {
			localizedValue = this.localization.value;
			// No localization, but we have a default value
		} else if ((this.localization || {}).defaultValue) {
			localizedValue = this.localization.defaultValue;
		} else if (this.default) {
			localizedValue = this.default;
			// If this.el is undefined, it is our first time through
			// so get the native element text (or HTML)
		} else if (!this.el && this.element) {
			localizedValue = this.htmlAllowed
				? this.element.nativeElement.innerHTML
				: this.element.nativeElement.innerText;
		}

		this.togglePathStyle();

		// No default value, so use the default localization for the specific portal
		if (!localizedValue && localizedValue !== '' && isLocalizing) {
			const defaultLocalization = this.localizationService.findLocalization(
				`${this.locFeature}/System.Global.Content.NoLocalization`
			);
			if (defaultLocalization) {
				localizedValue = defaultLocalization.value;
			}
		}
		this.localizedValue = localizedValue;
		if (this.el) {
			this.renderer.setProperty(this.el, this.htmlAllowed ? 'innerHTML' : 'innerText', this.localizedValue);
		}
	}

	private toggleInlineLocalization(showing: boolean): void {
		if (!this.el) {
			this.renderContainer();
		}
		if (this.locReadonly) {
			return;
		}

		if (showing) {
			this.showInlineLocalization();
		} else {
			this.hideInlineLocalization();
		}
	}

	private renderContainer(): void {
		this.el = this.renderer.createElement('span');
		this.renderer.setProperty(this.el, this.htmlAllowed ? 'innerHTML' : 'innerText', this.localizedValue);
		this.togglePathStyle();
		this.renderer.setProperty(this.element.nativeElement, 'innerHTML', '');

		this.renderer.appendChild(this.element.nativeElement, this.el);
	}

	private togglePathStyle() {
		if (!this.el) {
			return;
		}
		this.renderer.removeStyle(this.el, 'text-transform');
		this.renderer.removeStyle(this.el, 'letter-spacing');
	}

	private showInlineLocalization(): void {
		this.localizationService
			.findLocalization$(`${this.locFeature}/System.LocalizeDirective.Button.LocalizeAria`)
			.pipe(take(1))
			.subscribe(localization => {
				const ariaLocalizedText: string = localization?.value || this.ariaDefaultText;

				this.renderer.setAttribute(this.el, 'tabindex', '0');
				this.renderer.setAttribute(this.el, 'role', 'button');
				this.renderer.setAttribute(this.el, 'aria-label', `${ariaLocalizedText} ${this.localizedValue}`);
				this.renderer.addClass(this.el, 'cm-localize-inline');
				this.renderer.addClass(this.el, `cm-localize-inline--${this.getInlineBorderColor()}`);

				if (this.localization) {
					this.clickListenerFn = this.clickListener();
					this.keyboardListenerFn = this.keyboardListener();
				}
				this.cd.detectChanges();
			});
	}

	private hideInlineLocalization(): void {
		this.renderer.removeAttribute(this.el, 'tabindex');
		this.renderer.removeAttribute(this.el, 'role');
		this.renderer.removeAttribute(this.el, 'aria-label');
		this.renderer.removeClass(this.el, 'cm-localize-inline');
		this.renderer.removeClass(this.el, `cm-localize-inline--${this.getInlineBorderColor()}`);

		if (this.clickListenerFn) {
			this.clickListenerFn();
			this.keyboardListenerFn();
		}
	}

	openLocalizationDialog(): void {
		this.localizationService.triggerLocalization({ ...this.localization, htmlAllowed: this.htmlAllowed });
	}

	ngOnDestroy(): void {
		if (this.clickListenerFn) {
			// Remove the listener for the dialog popup.
			this.clickListenerFn();
			this.keyboardListenerFn();
		}
	}

	private clickListener(): () => void {
		return this.renderer.listen(this.el, 'click', event => {
			if (event.altKey) {
				return;
			}
			event.stopPropagation();
			event.preventDefault();
			this.openLocalizationDialog();
			return false;
		});
	}

	private keyboardListener(): () => void {
		return this.renderer.listen(this.el, 'keydown', (event: KeyboardEvent) => {
			if (event.key === ' ' || event.key === 'Enter') {
				event.stopPropagation();
				event.preventDefault();
				this.openLocalizationDialog();
				return false;
			}

			if (event.key === 'Escape') {
				event.stopPropagation();
				event.preventDefault();
				this.localizationService.toggleLocalization();
				return false;
			}
		});
	}

	private getInlineBorderColor(): string {
		// These strings must match the classes in the _cm-localize.scss file
		return !this.localization ? 'red' : this.htmlAllowed ? 'orange' : 'green';
	}
}
